import { toast } from "sonner";
import { Apple, Play, Smartphone, Bell, QrCode } from "lucide-react";
import Reveal from "@/components/Reveal";
import Logo from "@/components/Logo";

const REQS = [
  { platform: "iOS", os: "iOS 15 or later", size: "~120 MB", devices: "iPhone & iPad" },
  { platform: "Android", os: "Android 9 or later", size: "~110 MB", devices: "Phones & tablets" },
];

export default function Download() {
  const comingSoon = (store) =>
    toast.info(`${store} link goes live at launch. We're almost there!`);

  return (
    <div data-testid="download-page" className="pt-36">
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20 text-center">
        <Reveal>
          <p className="text-xs font-mono uppercase tracking-[0.3em] text-mint mb-4">
            Download
          </p>
          <h1 className="font-display text-6xl sm:text-7xl lg:text-8xl tracking-tight leading-none">
            GET IN <span className="text-volt">THE GAME</span>
          </h1>
          <p className="mt-6 max-w-xl mx-auto text-slate-300 leading-relaxed">
            Flappy Goal is finishing warm-ups and heading to the App Store and
            Google Play. Buttons light up on launch day.
          </p>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <button
              data-testid="download-app-store-button"
              onClick={() => comingSoon("App Store")}
              className="inline-flex items-center gap-3 rounded-2xl bg-white text-pitch px-7 py-4 font-bold transition-transform duration-200 hover:scale-105 active:scale-95"
            >
              <Apple size={26} />
              <span className="text-left leading-tight">
                <span className="block text-[10px] font-medium uppercase tracking-wide">
                  Download on the
                </span>
                <span className="text-lg">App Store</span>
              </span>
            </button>
            <button
              data-testid="download-google-play-button"
              onClick={() => comingSoon("Google Play")}
              className="inline-flex items-center gap-3 rounded-2xl border border-line bg-turf px-7 py-4 font-bold transition-transform duration-200 hover:scale-105 active:scale-95"
            >
              <Play size={24} className="text-volt" fill="#84CC16" />
              <span className="text-left leading-tight">
                <span className="block text-[10px] font-medium uppercase tracking-wide text-slate-400">
                  Get it on
                </span>
                <span className="text-lg">Google Play</span>
              </span>
            </button>
          </div>
          <p className="mt-4 text-xs font-mono uppercase tracking-widest text-slate-500" data-testid="download-placeholder-note">
            Store links are placeholders until launch day
          </p>
        </Reveal>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24 grid md:grid-cols-2 gap-8">
        <Reveal>
          <div
            data-testid="download-qr-card"
            className="h-full rounded-3xl border border-line bg-turf/70 p-10 flex flex-col items-center text-center"
          >
            <div className="w-40 h-40 rounded-2xl border-2 border-dashed border-volt/40 bg-pitch flex flex-col items-center justify-center gap-3">
              <QrCode size={40} className="text-volt/60" />
              <Logo size={30} />
            </div>
            <h2 className="mt-7 font-head text-2xl font-bold">Scan &amp; play</h2>
            <p className="mt-3 text-sm text-slate-400 leading-relaxed max-w-xs">
              A QR code will appear here at launch — point your phone camera at
              it and you'll be flapping in seconds.
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <div
            data-testid="download-requirements-card"
            className="h-full rounded-3xl border border-line bg-turf/70 p-10"
          >
            <div className="flex items-center gap-3">
              <Smartphone size={22} className="text-volt" />
              <h2 className="font-head text-2xl font-bold">System requirements</h2>
            </div>
            <div className="mt-7 space-y-4">
              {REQS.map((r) => (
                <div
                  key={r.platform}
                  data-testid={`requirements-${r.platform.toLowerCase()}`}
                  className="rounded-2xl border border-line bg-pitch/60 p-5"
                >
                  <p className="font-head font-bold text-lg text-volt">{r.platform}</p>
                  <dl className="mt-3 grid grid-cols-3 gap-3 text-sm">
                    <div>
                      <dt className="text-[10px] font-mono uppercase tracking-widest text-slate-500">
                        OS
                      </dt>
                      <dd className="mt-1 text-slate-300">{r.os}</dd>
                    </div>
                    <div>
                      <dt className="text-[10px] font-mono uppercase tracking-widest text-slate-500">
                        Size
                      </dt>
                      <dd className="mt-1 text-slate-300">{r.size}</dd>
                    </div>
                    <div>
                      <dt className="text-[10px] font-mono uppercase tracking-widest text-slate-500">
                        Devices
                      </dt>
                      <dd className="mt-1 text-slate-300">{r.devices}</dd>
                    </div>
                  </dl>
                </div>
              ))}
            </div>
            <p className="mt-6 flex items-center gap-2.5 text-sm text-slate-400">
              <Bell size={15} className="text-gold shrink-0" />
              Low storage? The game streams extra stadiums only when you need them.
            </p>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
